// tutorial: in questo package esponiamo le funzioni per fare le chiamate verso Magento

import axios, { AxiosRequestConfig } from 'axios';
import store from '@/store';
import { get as dataGetter } from 'lodash';
import { err } from '@/commons/toast';
import { i18n } from '@/i18n/index';
import { redirectToLocation } from '@/commons/redirectManager';
import {
    EstimateShippingRequest,
    LoginRequest,
    Customer,
    Totals,
    CartItem,
    Gift,
    UpdateCartRequest,
    ShippingMethod,
    PaymentMethod,
    UpdatePaymentRequest,
    PaymentInformationRequest,
    AssignAddressRequest,
    CartResponse,
    UpdateGiftRequest,
    ShippingInformationRequest as ShipInfoReq,
    ShippingInformationResponse as ShipInfoResp,
    SaveNewsletterResponse,
    RegistrationRequest,
    UserRegistrationRequest,
    CartInfo,
    QuoteAddress,
    AddToWishlistRequest,
    RequestInvoicePayload,
    PromoCarouselProduct,
    AddToCartRequest,
} from './models';

const baseURL = process.env.NODE_ENV === 'production' ?
    `${store.state.options.base_url}rest/${store.state.options.store_code}/V1` :
    `/rest/${store.state.options.store_code}/V1`;

function cartUrl(endpoint: string): string {
    const base = '';
    if (process.env.NODE_ENV !== 'production') {
        const appCartId = process.env.VUE_APP_CARTID;
        if (process.env.VUE_APP_FLOW === 'guest') {
            return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', appCartId);
        } else {
            if ((process.env.VUE_APP_FLOW === 'loggedsess' && localStorage.getItem('usertoken'))
                || process.env.VUE_APP_FLOW === 'logged'
            ) {
                return base + endpoint.replace(':cartApi', 'carts').replace(':cartId', 'mine');
            } else {
                return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', appCartId);
            }
        }
    } else {
        if (store.getters['user/isLoggedin']) {
            return base + endpoint.replace(':cartApi', 'carts').replace(':cartId', 'mine');
        } else {
            return base + endpoint.replace(':cartApi', 'guest-carts').replace(':cartId', store.getters['cart/token']);
        }
    }
}

function buildControllerRestClient() {
    return axios.create({
        baseURL: store.state.options.base_url,
        withCredentials: true,
    });
}

const cancelTokenSource = axios.CancelToken.source();
export const restClient = axios.create({
    baseURL,
    withCredentials: true,
    cancelToken: cancelTokenSource.token,
    headers: {'X-VUECHECKOUT': '1'},
});

const userUniqId = Math.random().toString(36).substr(2, 9);
const tabId = sessionStorage ?
    (sessionStorage.tabID ?
        sessionStorage.tabID :
        sessionStorage.tabID = Math.random().toString(36).substr(2, 9)
    ) : '';

const pendingRequests: any = {};
const flags = { handledUnauthorized: false };

function _sleepUntillPending(pendingKey: string, resolveCb: any, depsConfig: any): any {
    let allDepsFinished = true;
    if (depsConfig.hasOwnProperty(pendingKey)) {
        const deps = depsConfig[pendingKey] as string[];
        deps.forEach((dep) => {
            allDepsFinished = allDepsFinished
                && (!pendingRequests.hasOwnProperty(dep) || pendingRequests[dep] === 0);
        });
    }

    if (resolveCb) {
        if (allDepsFinished) {
            resolveCb();
        } else {
            setTimeout(() => {
                _sleepUntillPending(pendingKey, resolveCb, depsConfig);
            }, 500);
        }
    } else {
        return new Promise((resolve) => setTimeout(() => {
            if (!allDepsFinished) {
                let logstr = '';
                if (depsConfig.hasOwnProperty(pendingKey)) {
                    const deps = depsConfig[pendingKey] as string[];
                    deps.forEach((dep) => {
                        if (pendingRequests.hasOwnProperty(dep) && pendingRequests[dep] > 0) {
                            logstr += ' DEP ' + dep + ' has ' + pendingRequests[dep] + ' pending request(s)';
                        }
                    });
                }
                console.log('Pending deps for ' + pendingKey + ':' + logstr);
                _sleepUntillPending(pendingKey, resolve, depsConfig);
            } else {
                resolve({});
            }
        }, 1000));
    }
}

function getCleanedStore() {
    const s: any = store.state;
    const storeRes = {
        checkoutEmail: dataGetter(s, 'checkout.email'),
        billingId: dataGetter(s, 'checkout.billingAddress.id'),
        shippingId: dataGetter(s, 'checkout.shippingAddress.id'),
        selectedPayment: dataGetter(s, 'checkout.selectedPayment'),
        selectedShip: dataGetter(s, 'checkout.selectedShipment'),
        invoice: dataGetter(s, 'checkout.requestInvoice.cfpiva'),
        coupon: dataGetter(s, 'checkout.coupon.coupon'),
        userId: dataGetter(s, 'user.customer.id'),
        cartToken: dataGetter(s, 'cart.cartToken'),
        cartId: dataGetter(s, 'cart.cart.id'),
        fidelity: dataGetter(s, 'fidelitycard.fidelityCard'),
        payment: {
            registry: dataGetter(s, 'payment.registry'),
            cc: {
                cardEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.number.isEmpty'),
                cardValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.number.isValid'),
                cvvEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.cvv.isEmpty'),
                cvvValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.cvv.isValid'),
                expEmpty: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.expirationDate.isEmpty'),
                expValid: dataGetter(s, 'payment.methodData[1].hostedFields._state.fields.expirationDate.isValid'),
            },
            sale: dataGetter(s, 'payment.salePayload'),
        },
    };
    return storeRes;
}

export const registeredInterceptors: any = {
    'intercept-request-uniqusertoken': restClient.interceptors.request.use((config) => {
        const s: any = store.state;
        const token = dataGetter(s, 'cart.cart.id') + '-' + userUniqId + '-' + tabId;
        Object.assign(config.headers, {'X-UNIQUSERTOKEN': token});
        return config;
    }),
    'intercept-request-mergecart': restClient.interceptors.request.use((config) => {
        if (config.url && config.url.indexOf('integration/customer/token') !== -1) {
            Object.assign(config.headers, {'X-MERGECARTSTOKEN': config.data.cartToken});
        }
        return config;
    }),
    'intercept-response-handleUnauthorized': restClient.interceptors.response.use((response) => {
        return response;
    }, (error) => {
        const notAffected = ['integration/customer/token'];
        if (process.env.NODE_ENV === 'production'
            && error.response.status === 401
            && !flags.handledUnauthorized
            && !notAffected.includes(error.config.url)
        ) {
            flags.handledUnauthorized = true;
            cancelTokenSource.cancel();
            err(i18n.t('Session expired, page will reload').toString());
            setTimeout(() => {
                redirectToLocation('', true);
            }, 500);
        }
        return Promise.reject(error);
    }),
    'debug-request-debugmodeheader': restClient.interceptors.request.use((config) => {
        if (process.env.NODE_ENV !== 'production') {
            Object.assign(config.headers, {'X-DEBUGMODE': 1, 'X-CARTID': dataGetter(store.state, 'cart.cart.id')});
        }
        return config;
    }),
    'debug-request-addpending': restClient.interceptors.request.use((config) => {
        if (process.env.NODE_ENV !== 'production') {
            const url = config.url as string;
            if (!pendingRequests.hasOwnProperty(url)) {
                pendingRequests[url] = 0;
            }
            pendingRequests[url]++;
        }
        return config;
    }),
    'debug-request-waitresolveuser': restClient.interceptors.request.use((config) => {
        const url = config.url as string;
        const affected = ['carts/mine/totals'];
        const deps = {
            'carts/mine/totals': ['integration/customer/token'],
        };
        if (process.env.NODE_ENV !== 'production' && affected.includes(url)) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    _sleepUntillPending(url, null, deps).then(() => {
                        Object.assign(config.headers, restClient.defaults.headers);
                        resolve(config);
                    });
                }, 500);
            });
        } else {
            return config;
        }
    }),
    'debug-response-removepending': restClient.interceptors.response.use((response) => {
        if (process.env.NODE_ENV !== 'production') {
            const url = response.config.url as string;
            if (pendingRequests.hasOwnProperty(url)) {
                pendingRequests[url]--;
            }
        }
        return response;
    }, (error) => {
        if (process.env.NODE_ENV !== 'production') {
            const url = error.config.url as string;
            if (pendingRequests.hasOwnProperty(url)) {
                pendingRequests[url]--;
            }
        }
        return Promise.reject(error);
    }),
};

if (process.env.NODE_ENV === 'production') {
    for (const p in registeredInterceptors) {
        if (p.indexOf('debug-request') !== -1) {
            restClient.interceptors.request.eject(registeredInterceptors[p]);
        } else if (p.indexOf('debug-response') !== -1) {
            restClient.interceptors.response.eject(registeredInterceptors[p]);
        }
    }
}

// function logPendingRequests() {
//     console.log(pendingRequests);
// }
// logPendingRequests();
// setInterval(logPendingRequests, 500);

export async function mockData(email?: string): Promise<any> {
    const resp = await restClient.get(email ? 'mockdata/' + encodeURIComponent(email) : 'mockdata');
    return JSON.parse(resp.data);
}

export async function saveLog(log: string): Promise<string> {
    const uniqId = Date.now() + '_' + Math.random().toString().slice(2);
    const payload = {
        log,
        store: getCleanedStore(),
        uniqId,
    };
    const resp = await restClient.post('saveLog', payload);
    return resp.data;
}

export function setJWT(token: string): void {
    if (process.env.VUE_APP_FLOW === 'loggedsess') {
        localStorage.setItem('usertoken', token);
    }
    restClient.defaults.headers.Authorization = `Bearer ${token}`;
}

export function clearJWT(): void {
    delete restClient.defaults.headers.Authorization;
}

export async function login(request: LoginRequest): Promise<string> {
    const resp = await restClient.post('integration/customer/token', request);
    return resp.data;
}

export async function forgotPassword(email: string): Promise<boolean> {
    const template = 'email_reminder';
    const resp = await restClient.put('customers/password', {email, template});
    return resp.data;
}

export async function userRegistration(request: RegistrationRequest): Promise<boolean> {
    const resp = await restClient.post('customers', {customer: request.user, password: request.password});
    return resp.data;
}

export async function userSaveData(request: UserRegistrationRequest): Promise<Customer> {
    const resp = await restClient.put('customers/me', {customer: request});
    return resp.data;
}

export async function deleteCustomerAddress(addressId: number): Promise<Customer> {
    const resp = await restClient.delete('customers/me/deleteAddress/'+addressId);
    return resp.data;
}

export async function isEmailAvailable(email: string): Promise<boolean> {
    const resp = await restClient.post('customers/isEmailAvailable', {customerEmail: email});
    return resp.data;
}

export async function saveNewsletterAgreement(email: string): Promise<SaveNewsletterResponse> {
    const ep = 'newsletter/register';
    const resp = await restClient.post(ep, { email });
    return resp.data;
}

export async function getUserInfo(): Promise<Customer> {
    const resp = await restClient.get('customers/me');
    return (resp.data as Customer);
}

export async function getCart(): Promise<CartResponse> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId'));
    return (resp.data as CartResponse);
}

export async function assignEmailToQuote(email: string): Promise<boolean> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/assignEmail`), { email });
    return (resp.data);
}

export async function addToCart(request: AddToCartRequest): Promise<CartItem> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/items`), request);
    return (resp.data as CartItem);
}

export async function updateCartQty(request: UpdateCartRequest, itemId: number): Promise<CartItem> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/items/${itemId}`), request);
    return (resp.data as CartItem);
}

export async function deleteCartItem(itemId: number): Promise<boolean> {
    const resp = await restClient.delete(cartUrl(`:cartApi/:cartId/items/${itemId}`));
    return (resp.data as boolean);
}

export async function getTotals(): Promise<Totals> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/totals'));
    return (resp.data as Totals);
}

export async function saveRequestInvoice(payload: RequestInvoicePayload): Promise<RequestInvoicePayload> {
    const resp = await restClient.put(cartUrl(':cartApi/:cartId/set-order-requireinvoice-fields'), payload);
    return (resp.data as RequestInvoicePayload);
}

export async function addItemToWishlist(request: AddToWishlistRequest): Promise<any> {
    const resp = await restClient.post('tdyWishlist/me/add', request);
    return resp.data;
}

export async function estimateShipping(request: EstimateShippingRequest): Promise<ShippingMethod[]> {
    // workaround per bug magento, se passiamo gli extension attributes questa chiamata è buggata, vedi
    // htdocs/vendor/magento/module-quote/Model/ShippingMethodManagement.php:300
    // $shippingAddress->addData($this->extractAddressData($address));
    if (request.address.hasOwnProperty('extension_attributes')) {
        delete request.address.extension_attributes;
    }

    const resp = await restClient.post(cartUrl(':cartApi/:cartId/estimate-shipping-methods'), request);
    return (resp.data as ShippingMethod[]);
}

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/payment-methods'));
    return (resp.data as PaymentMethod[]);
}

export async function getCoupon(): Promise<any> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/coupons'));
    return resp.data;
}

export async function doApplyCoupon(couponCode: string): Promise<boolean> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/coupons/${couponCode}`));
    return (resp.data as boolean);
}

export async function deleteCoupon(): Promise<boolean> {
    const resp = await restClient.delete(cartUrl(':cartApi/:cartId/coupons/'));
    return (resp.data as boolean);
}

export async function assignAddress(request: AssignAddressRequest): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/billing-address'), request);
    return (resp.data as string);
}

export async function updateShippingAddress(address: QuoteAddress): Promise<string> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/updateShippingAddress'), { address });
    return (resp.data as string);
}

export async function getSelectedPaymentMethod(): Promise<string> {
    const resp = await restClient.get(cartUrl(':cartApi/:cartId/selected-payment-method'));
    return (resp.data.method as string);
}

export async function setPaymentMethod(request: UpdatePaymentRequest): Promise<string> {
    const resp = await restClient.put(cartUrl(':cartApi/:cartId/selected-payment-method'), request);
    return (resp.data as string);
}

export async function setShippingInformation(request: ShipInfoReq): Promise<ShipInfoResp> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/shipping-information'), request);
    return (resp.data as ShipInfoResp);
}

export async function setPickupStore(pickupStoreId: string): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/setPickupStore'), { pickupStoreId });
    return resp.data;
}

export async function deletePickupStore(): Promise<any> {
    const resp = await restClient.delete(cartUrl(':cartApi/:cartId/deletePickupStore'));
    return resp.data;
}

export async function setPickupPoint(pickupId: string, pickupData: any): Promise<any> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/pikuppoints'), { pickupId, pickupData });
    return resp.data;
}

export async function deletePickupPoint(): Promise<any> {
    const resp = await restClient.delete(cartUrl(':cartApi/:cartId/pikuppoints'));
    return resp.data;
}

export async function setPaymentInformation(request: PaymentInformationRequest): Promise<number> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/payment-information'), request);
    return (parseInt(resp.data, 10) as number);
}

export async function setPaymentInformationWithoutPlace(request: PaymentInformationRequest): Promise<number> {
    const resp = await restClient.post(cartUrl(':cartApi/:cartId/set-payment-information'), request);
    return (parseInt(resp.data, 10) as number);
}

export async function getYandexPayment(type: string): Promise<any> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/createYandexPayment/${type}`));
    return resp.data;
}

// tslint:disable-next-line:max-line-length
export async function getHipayPayment(type: string, shouldTokenizeCard: boolean, tokenizedCardId?: number): Promise<string> {
    const getParams: any = {};
    if (navigator) {
        getParams.java_enabled = navigator.javaEnabled();
        getParams.language = navigator.language;
    }
    if (window && window.screen) {
        getParams.color_depth = window.screen.colorDepth;
        getParams.screen_height = window.screen.height;
        getParams.screen_width = window.screen.width;
        getParams.timezone = new Date().getTimezoneOffset();
    }
    getParams.tokenize_card = shouldTokenizeCard ? 1 : 0;

    if (tokenizedCardId) {
        getParams.tokenized_card_id = tokenizedCardId;
    }

    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/createHiPayPayment/${type}`), {
        params: getParams,
    });
    return resp.data;
}

export async function sendHipayTokenizedCard(cardData: any): Promise<string> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/tokenizedPayment`), { cardData });
    return resp.data;
}

export async function updateGift(request: UpdateGiftRequest): Promise<Gift> {
    const resp = await restClient.put(cartUrl(`:cartApi/:cartId/updateGift`), request);
    return (resp.data as Gift);
}

export async function removeGift(): Promise<Gift> {
    const resp = await restClient.delete(cartUrl(`:cartApi/:cartId/removeGift`));
    return (resp.data as Gift);
}

export async function blackboxCheckPromoOnTicket(): Promise<CartInfo> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/checkPromoOnTicket`));
    return (resp.data as CartInfo);
}

export async function collectCartInfo(infos: string[]): Promise<CartInfo> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/collectCartInfo/${infos.join(',')}`));
    return (resp.data as CartInfo);
}

export async function getPromoCarouselProducts(promoId: string): Promise<PromoCarouselProduct[]> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/tdypromoCarousel/${promoId}`));
    return (resp.data as PromoCarouselProduct[]);
}

export async function getPromoCarouselProduct(productId: string): Promise<PromoCarouselProduct> {
    const resp = await restClient.get(`tdypromo/carouselProduct/${productId}`);
    return (resp.data as PromoCarouselProduct);
}

export async function getReservedOrderId(): Promise<string> {
    const ep = 'get-reserved-order-id';
    const resp = await restClient.get(ep);
    return (resp.data as string);
}

export async function getFidelity(): Promise<any> {
    const resp = await restClient.post('fidelity/points');
    return resp.data;
}

export async function doUseBonus(bonus: number): Promise<number> {
    const resp = await restClient.post('fidelity/use', {points: bonus});
    return resp.data;
}

export async function getAppliedBonus(): Promise<any> {
    const resp = await restClient.post('fidelity/appliedPoints');
    return resp.data;
}

export async function getAmazonShippingAddress(amazonAccessToken: string, amazonOrderReference: string): Promise<any> {
    const payload = {addressConsentToken: amazonAccessToken};
    const resp = await restClient.put(`amazon-shipping-address/${amazonOrderReference}`, payload);
    return resp.data;
}

export async function setAmazonBillingAddress(amazonAccessToken: string, amazonOrderReference: string): Promise<any> {
    const payload = {addressConsentToken: amazonAccessToken};
    const resp = await restClient.put(`amazon-billing-address/${amazonOrderReference}`, payload);
    return resp.data;
}

export async function isSessionValid(): Promise<boolean> {
    const resp = await restClient.get(cartUrl(`:cartApi/:cartId/isSessionValid`));
    return resp.data;
}

export async function klarnaWaitAuthCallback(authToken: string): Promise<any> {
    const controllerClient = buildControllerRestClient();
    const resp = await controllerClient.post('checkout/klarna/quoteStatus', {authorization_token: authToken});
    return resp.data;
}

export async function klarnaUpdateSession(authToken: string): Promise<any> {
    const controllerClient = buildControllerRestClient();
    const resp = await controllerClient.post('checkout/klarna/updateSession', {authorization_token: authToken});
    return resp.data;
}

// API wrapper per la gift
export async function getGiftCardCredit(code: string): Promise<any> {
    const resp = await restClient.get(`giftcard/${code}/credit`);
    return resp.data;
}

export async function applyGiftCard(code: string, amount: any): Promise<any> {
    const req = cartUrl(':cartApi/:cartId/applyGiftCard/');
    const payload = {code, amount};

    const resp = await restClient.put(req, payload);
    return resp.data;
}

export async function removeGiftCard(code: string): Promise<any> {
    const req = cartUrl(':cartApi/:cartId/removeGiftCard/');
    const payload = {code};

    const resp = await restClient.put(req, payload);
    return resp.data;
}

export async function validateBeforePlace(billingAddress: QuoteAddress, email: string): Promise<boolean> {
    const resp = await restClient.post(cartUrl(`:cartApi/:cartId/checkAddressBeforePay`), {billingAddress, email});
    return resp.data;
}

export async function giftcardValidateBeforePlace(billingAddress: QuoteAddress, email: string): Promise<boolean> {
    const resp = await restClient.post(
        cartUrl(`:cartApi/:cartId/giftCardCheckAddressBeforePay`), {billingAddress, email},
    );
    return resp.data;
}
